import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Breadcrumb from '../../components/feature/Breadcrumb';
import RelatedPosts from '../../components/feature/RelatedPosts';
import FAQSection from '../../components/feature/FAQSection';
import { articles } from '../../mocks/articles';

const ArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find(a => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Không tìm thấy bài viết</h1>
          <Link to="/" className="text-accent-cyan hover:underline">
            Quay về trang chủ
          </Link>
        </div>
      </div>
    );
  }

  // Article Schema
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image,
    "datePublished": article.date,
    "dateModified": article.date,
    "author": {
      "@type": "Person",
      "name": article.author.name,
      "url": `https://gametv.asia/tac-gia/${article.author.name.toLowerCase().replace(/\s+/g, '-')}`
    },
    "publisher": {
      "@type": "Organization",
      "name": "GameTV.asia",
      "logo": {
        "@type": "ImageObject",
        "url": "https://gametv.asia/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://gametv.asia/bai-viet/${article.slug}`
    }
  };

  // Person Schema for game thủ articles
  const personSchema = article.category === 'Game Thủ' ? {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": article.title.split(':')[0].trim(),
    "description": article.excerpt,
    "image": article.image,
    "url": `https://gametv.asia/bai-viet/${article.slug}`
  } : null;

  const breadcrumbItems = [
    { label: 'Trang chủ', path: '/' },
    { label: article.game, path: `/${article.game.toLowerCase().replace(/\s+/g, '-')}` },
    { label: article.title }
  ];

  const faqs = [
    {
      question: `${article.title.split(':')[0]} là ai?`,
      answer: `${article.title.split(':')[0]} là một trong những game thủ nổi tiếng nhất tại Việt Nam trong lĩnh vực ${article.game}. Với tài năng xuất chúng và sự nỗ lực không ngừng nghỉ, họ đã tạo dựng được tên tuổi và trở thành hình mẫu cho nhiều game thủ trẻ.`
    },
    {
      question: `Thành tích nổi bật của ${article.title.split(':')[0]} là gì?`,
      answer: `${article.title.split(':')[0]} đã đạt được nhiều thành tích ấn tượng trong sự nghiệp, bao gồm các chức vô địch quốc gia, giải đấu khu vực và thậm chí cả đấu trường quốc tế. Chi tiết về các thành tích được trình bày đầy đủ trong bài viết.`
    },
    {
      question: `Làm thế nào để chơi ${article.game} giỏi như ${article.title.split(':')[0]}?`,
      answer: `Để đạt được trình độ cao trong ${article.game}, bạn cần luyện tập đều đặn, học hỏi từ các game thủ chuyên nghiệp, nắm vững meta game hiện tại, và không ngừng cải thiện kỹ năng cá nhân. Kiên trì và đam mê là chìa khóa thành công.`
    },
    {
      question: `${article.title.split(':')[0]} stream ở đâu?`,
      answer: `Bạn có thể theo dõi ${article.title.split(':')[0]} trên các nền tảng streaming phổ biến như Facebook Gaming, YouTube Gaming, hoặc Twitch. Thông tin chi tiết về lịch stream và kênh chính thức được cập nhật trong bài viết.`
    },
    {
      question: `Cộng đồng ${article.game} Việt Nam như thế nào?`,
      answer: `Cộng đồng ${article.game} tại Việt Nam rất sôi động và đông đảo, với hàng triệu người chơi và fan hâm mộ. Có nhiều giải đấu, sự kiện và hoạt động cộng đồng diễn ra thường xuyên, tạo nên một môi trường gaming sôi động và chuyên nghiệp.`
    }
  ];

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      {personSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
        />
      )}

      <Header />

      <main>
        {/* Hero Image */}
        <div className="relative h-[60vh] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-dark-bg"></div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-6 -mt-32 relative z-10">
          {/* Breadcrumb */}
          <Breadcrumb items={breadcrumbItems} />

          {/* Article Header */}
          <header className="bg-dark-card/80 backdrop-blur-lg border border-gray-800 rounded-2xl p-8 mb-8">
            {/* Category Badge */}
            <Link
              to={`/${article.game.toLowerCase().replace(/\s+/g, '-')}`}
              className="inline-block px-4 py-2 bg-gradient-gaming text-white text-sm font-semibold rounded-full mb-4 hover:scale-105 transition-transform whitespace-nowrap"
            >
              {article.game}
            </Link>

            {/* Title */}
            <h1 className="font-exo font-black text-4xl md:text-5xl text-white mb-6 leading-tight">
              {article.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400 mb-6">
              <div className="flex items-center gap-2">
                <img
                  src={article.author.avatar}
                  alt={article.author.name}
                  className="w-10 h-10 rounded-full border-2 border-accent-cyan"
                />
                <div>
                  <Link
                    to={`/tac-gia/${article.author.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-white hover:text-accent-cyan transition-colors font-semibold"
                  >
                    {article.author.name}
                  </Link>
                  <div className="text-xs text-gray-500">{article.author.role}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                <time dateTime={article.date}>{article.date}</time>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                <span>{article.readTime}</span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                <span>{article.views} lượt xem</span>
              </div>
            </div>

            {/* Excerpt */}
            <p className="text-gray-300 text-lg leading-relaxed border-l-4 border-accent-cyan pl-4">
              {article.excerpt}
            </p>
          </header>

          {/* Article Body */}
          <div className="bg-dark-card/80 backdrop-blur-lg border border-gray-800 rounded-2xl p-8 mb-8">
            <div className="prose prose-invert prose-lg max-w-none">
              <p className="text-gray-300 leading-relaxed mb-6">
                Trong làng game Việt Nam, <strong className="text-white">{article.title.split(':')[0]}</strong> là cái tên không còn xa lạ với những ai yêu thích <Link to={`/${article.game.toLowerCase().replace(/\s+/g, '-')}`} className="text-accent-cyan hover:text-accent-cyan/80 underline">{article.game}</Link>. Với tài năng xuất chúng và sự nỗ lực không ngừng nghỉ, họ đã tạo dựng được một sự nghiệp đáng ngưỡng mộ và trở thành nguồn cảm hứng cho hàng triệu game thủ trẻ.
              </p>

              <h2 className="font-exo font-bold text-3xl text-white mt-12 mb-6 flex items-center gap-3">
                <div className="w-1 h-8 bg-gradient-gaming"></div>
                Hành Trình Khởi Đầu
              </h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                Bắt đầu từ những ngày đầu tiên tiếp xúc với <Link to={`/${article.game.toLowerCase().replace(/\s+/g, '-')}`} className="text-accent-cyan hover:text-accent-cyan/80 underline">{article.game}</Link>, <strong className="text-white">{article.title.split(':')[0]}</strong> đã thể hiện được tài năng thiên bẩm và niềm đam mê mãnh liệt với game. Không chỉ dừng lại ở việc chơi game giải trí, họ đã quyết tâm theo đuổi con đường chuyên nghiệp và không ngừng rèn luyện kỹ năng mỗi ngày.
              </p>
              <p className="text-gray-300 leading-relaxed mb-6">
                Những giờ luyện tập cật lực, việc nghiên cứu meta game, phân tích đối thủ và học hỏi từ các game thủ hàng đầu thế giới đã giúp <strong className="text-white">{article.title.split(':')[0]}</strong> nhanh chóng vươn lên và tạo dựng tên tuổi trong cộng đồng game thủ Việt Nam.
              </p>

              <h2 className="font-exo font-bold text-3xl text-white mt-12 mb-6 flex items-center gap-3">
                <div className="w-1 h-8 bg-gradient-gaming"></div>
                Thành Tích Nổi Bật
              </h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                Trong suốt sự nghiệp của mình, <strong className="text-white">{article.title.split(':')[0]}</strong> đã đạt được vô số thành tích ấn tượng. Từ các giải đấu trong nước cho đến đấu trường quốc tế, họ luôn để lại dấu ấn đậm nét và khẳng định vị thế của game thủ Việt Nam trên bản đồ esports thế giới.
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-3 mb-6 ml-4">
                <li>Vô địch giải đấu quốc gia nhiều lần liên tiếp</li>
                <li>Top đầu bảng xếp hạng khu vực Đông Nam Á</li>
                <li>Tham gia các giải đấu quốc tế lớn</li>
                <li>Được công nhận là một trong những game thủ xuất sắc nhất Việt Nam</li>
                <li>Có lượng fan hâm mộ đông đảo trên các nền tảng mạng xã hội</li>
              </ul>

              <h2 className="font-exo font-bold text-3xl text-white mt-12 mb-6 flex items-center gap-3">
                <div className="w-1 h-8 bg-gradient-gaming"></div>
                Phong Cách Chơi Đặc Trưng
              </h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                Điều làm nên sự khác biệt của <strong className="text-white">{article.title.split(':')[0]}</strong> chính là phong cách chơi độc đáo và sáng tạo. Không theo khuôn mẫu, họ luôn tìm tòi những chiến thuật mới, những cách chơi bất ngờ khiến đối thủ phải bất ngờ và khán giả phải trầm trồ.
              </p>
              <p className="text-gray-300 leading-relaxed mb-6">
                Sự kết hợp giữa kỹ thuật điêu luyện, tư duy chiến thuật sắc bén và khả năng đọc game xuất sắc đã giúp <strong className="text-white">{article.title.split(':')[0]}</strong> trở thành một trong những game thủ được yêu thích và ngưỡng mộ nhất tại Việt Nam.
              </p>

              <h2 className="font-exo font-bold text-3xl text-white mt-12 mb-6 flex items-center gap-3">
                <div className="w-1 h-8 bg-gradient-gaming"></div>
                Ảnh Hưởng Đến Cộng Đồng
              </h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                Không chỉ là một game thủ tài năng, <strong className="text-white">{article.title.split(':')[0]}</strong> còn là một người truyền cảm hứng cho hàng triệu game thủ trẻ. Thông qua các buổi stream, video hướng dẫn và chia sẻ kinh nghiệm, họ đã góp phần phát triển cộng đồng <Link to={`/${article.game.toLowerCase().replace(/\s+/g, '-')}`} className="text-accent-cyan hover:text-accent-cyan/80 underline">{article.game}</Link> Việt Nam ngày càng lớn mạnh.
              </p>
              <p className="text-gray-300 leading-relaxed mb-6">
                Với thái độ chuyên nghiệp, tinh thần fair-play và sự tận tâm với cộng đồng, <strong className="text-white">{article.title.split(':')[0]}</strong> đã trở thành hình mẫu lý tưởng cho những ai muốn theo đuổi con đường esports chuyên nghiệp.
              </p>

              <h2 className="font-exo font-bold text-3xl text-white mt-12 mb-6 flex items-center gap-3">
                <div className="w-1 h-8 bg-gradient-gaming"></div>
                Kết Luận
              </h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                <strong className="text-white">{article.title.split(':')[0]}</strong> không chỉ là một game thủ xuất sắc mà còn là biểu tượng của sự nỗ lực, đam mê và tài năng trong làng game Việt Nam. Hành trình của họ là minh chứng cho việc với sự kiên trì và nỗ lực không ngừng, bất kỳ ai cũng có thể đạt được ước mơ của mình.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Hãy tiếp tục theo dõi <Link to="/" className="text-accent-cyan hover:text-accent-cyan/80 underline">GameTV.asia</Link> để cập nhật những tin tức mới nhất về <strong className="text-white">{article.title.split(':')[0]}</strong> và các game thủ hàng đầu Việt Nam khác!
              </p>
            </div>

            {/* Tags */}
            <div className="mt-12 pt-8 border-t border-gray-800">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-gray-400 font-semibold">Tags:</span>
                {[article.game, article.category, 'Game thủ Việt Nam', 'Esports', 'Gaming'].map((tag, index) => (
                  <Link
                    key={index}
                    to={`/tag/${tag.toLowerCase().replace(/\s+/g, '-')}`}
                    className="px-3 py-1 bg-gray-800 text-gray-300 text-sm rounded-full hover:bg-accent-cyan hover:text-white transition-colors whitespace-nowrap"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            {/* Share Buttons */}
            <div className="mt-8 pt-8 border-t border-gray-800">
              <div className="flex items-center gap-4">
                <span className="text-gray-400 font-semibold">Chia sẻ:</span>
                <button className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-full hover:scale-110 transition-transform">
                  <i className="ri-facebook-fill"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center bg-sky-500 text-white rounded-full hover:scale-110 transition-transform">
                  <i className="ri-twitter-fill"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center bg-red-600 text-white rounded-full hover:scale-110 transition-transform">
                  <i className="ri-youtube-fill"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center bg-gray-700 text-white rounded-full hover:scale-110 transition-transform">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Author Bio */}
          <div className="bg-dark-card/80 backdrop-blur-lg border border-gray-800 rounded-2xl p-8 mb-8">
            <div className="flex items-start gap-6">
              <img
                src={article.author.avatar}
                alt={article.author.name}
                className="w-24 h-24 rounded-full border-4 border-accent-cyan"
              />
              <div className="flex-1">
                <h3 className="font-exo font-bold text-2xl text-white mb-2">
                  {article.author.name}
                </h3>
                <p className="text-accent-cyan text-sm mb-3">{article.author.role}</p>
                <p className="text-gray-300 leading-relaxed">
                  Biên tập viên chuyên về {article.game} với hơn 5 năm kinh nghiệm trong ngành gaming. Đam mê esports và luôn cập nhật những tin tức mới nhất về làng game Việt Nam và thế giới.
                </p>
              </div>
            </div>
          </div>
        </article>

        {/* FAQ Section */}
        <FAQSection faqs={faqs} />

        {/* Related Posts */}
        <RelatedPosts posts={articles} currentPostId={article.id} />
      </main>

      <Footer />
    </div>
  );
};

export default ArticlePage;